import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import './App.css';

// Eagerly loaded components (above the fold)
import Navigation from './components/Navigation/Navigation';
import Hero from './components/Hero/Hero';

// Lazy loaded components (below the fold) for better performance
const TrustSection = lazy(() => import('./components/shared/TrustSection'));
const StatsSection = lazy(() => import('./components/shared/StatsSection'));
const Services = lazy(() => import('./components/Services/Services'));
const CaseStudies = lazy(() => import('./components/CaseStudies/CaseStudies'));
const Testimonials = lazy(() => import('./components/Testimonials/Testimonials'));
const FormsSection = lazy(() => import('./components/Forms/FormsSection'));
const FAQ = lazy(() => import('./components/FAQ/FAQ'));
const Blog = lazy(() => import('./components/Blog/Blog'));
const Newsletter = lazy(() => import('./components/shared/Newsletter'));
const Footer = lazy(() => import('./components/Footer/Footer'));
const CookieConsent = lazy(() => import('./components/shared/CookieConsent'));
const BackToTop = lazy(() => import('./components/shared/BackToTop'));
const PolicyModal = lazy(() => import('./components/shared/PolicyModal'));

// Loading fallback component
const SectionLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    color: 'var(--orange)'
  }}>
    <div className="spinner" style={{ width: '40px', height: '40px' }}></div>
  </div>
);

export default function GadesWebsite() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
        setSelectedCaseStudy(null);
        setSelectedService(null);
        setSelectedPolicy(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedCaseStudy || selectedService || selectedPolicy) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedCaseStudy, selectedService, selectedPolicy]);

  return (
    <div className="website-container" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Inline critical CSS - kept for performance */}
      <style>{`
        /* Font loaded in index.html with preconnect for better performance */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; max-width: 100%; overflow-x: hidden; -webkit-font-smoothing: antialiased; margin: 0; padding: 0; background: #000; color: #FFF; }
        :root {
          --orange: #E85A2A;
          --orange-hover: #D14A1E;
          --text-primary: #FFFFFF;
          --text-secondary: #B3B3B3;
          --text-muted: #808080;
          --bg-primary: #000000;
          --bg-secondary: #0a0a0a;
          --bg-card: rgba(255, 255, 255, 0.05);
          --border-subtle: rgba(255, 255, 255, 0.1);
          --focus-ring: 0 0 0 3px rgba(232, 90, 42, 0.4);
          --spacing-xs: 0.5rem;
          --spacing-sm: 1rem;
          --spacing-md: 1.5rem;
          --spacing-lg: 2rem;
          --spacing-xl: 3rem;
          --spacing-2xl: 4rem;
          --spacing-3xl: 6rem;
          --spacing-4xl: 8rem;
          --min-tap-target: 44px;
        }

        /* Accessibility: Focus visible styles */
        *:focus { outline: none; }
        *:focus-visible {
          outline: 2px solid var(--orange);
          outline-offset: 2px;
        }
        button:focus-visible, a:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible {
          box-shadow: var(--focus-ring);
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }

        .scroll-progress { position: fixed; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--orange), #FF7A45); transform-origin: 0%; z-index: 9998; }
        .nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; background: rgba(0, 0, 0, 0.95); backdrop-filter: blur(20px); border-bottom: 1px solid rgba(255, 255, 255, 0.1); }
        .nav-container { max-width: 1400px; margin: 0 auto; padding: 1.25rem 3rem; display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 3rem; }
        .logo { display: flex; align-items: center; gap: 1rem; cursor: pointer; transition: all 0.3s; background: none; border: none; padding: 0; }
        .logo:hover { opacity: 0.8; }
        .logo-image { height: 40px; width: auto; transition: transform 0.3s; }
        .logo:hover .logo-image { transform: scale(1.05); }
        .nav-links { display: flex; gap: 2.5rem; list-style: none; justify-content: center; }
        .nav-links button { background: none; border: none; color: #FFF; font-weight: 500; font-size: 0.875rem; cursor: pointer; transition: color 0.3s; position: relative; text-transform: uppercase; letter-spacing: 0.05em; font-family: inherit; padding: 0.5rem 0; }
        .nav-links button::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 2px; background: var(--orange); transition: width 0.3s; }
        .nav-links button:hover { color: var(--orange); }
        .nav-links button:hover::after { width: 100%; }
        .mobile-menu-btn { display: none; background: none; border: none; cursor: pointer; padding: 0.75rem; min-width: var(--min-tap-target); min-height: var(--min-tap-target); flex-direction: column; justify-content: center; align-items: center; gap: 5px; }
        .mobile-menu-btn span { display: block; width: 24px; height: 2px; background: #FFF; transition: all 0.3s; }
        .mobile-menu { position: fixed; top: 76px; right: 0; bottom: 0; width: 100%; max-width: 300px; background: #000; z-index: 999; padding: 2rem; }
        .mobile-menu button { display: block; width: 100%; text-align: left; background: none; border: none; color: #FFF; padding: 1rem 0; border-bottom: 1px solid rgba(255, 255, 255, 0.1); font-weight: 500; font-size: 1rem; font-family: inherit; cursor: pointer; min-height: var(--min-tap-target); transition: color 0.3s; }
        .mobile-menu button:hover { color: var(--orange); }

        .hero { min-height: 100vh; position: relative; padding-top: 76px; width: 100%; background-image: url('https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop&q=60&fm=webp'); background-size: cover; background-position: center; display: flex; align-items: center; }
        @media (min-width: 768px) { .hero { background-image: url('https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1600&h=900&fit=crop&q=80&fm=webp'); } }
        .hero::before { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.78) 50%, rgba(0,0,0,0.5) 100%); }
        .hero-content { position: relative; z-index: 2; max-width: 1400px; width: 100%; margin: 0 auto; padding: 4rem 3rem; }
        .hero-tag { color: var(--orange); text-transform: uppercase; font-size: 0.875rem; font-weight: 600; letter-spacing: 0.1em; margin-bottom: 2rem; }
        .hero-content h1 { font-size: 4.5rem; line-height: 1.1; letter-spacing: -0.03em; font-weight: 700; margin-bottom: 2rem; color: #FFF; max-width: 800px; }
        .highlight { color: var(--orange); }
        .hero-content p { font-size: 1.125rem; line-height: 1.7; color: #E5E5E5; margin-bottom: 2rem; max-width: 600px; }
        .hero-cta { background: var(--orange); color: #FFF; padding: 1.125rem 2.5rem; border: none; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.3s; box-shadow: 0 8px 24px rgba(232, 90, 42, 0.3); text-transform: uppercase; letter-spacing: 0.05em; }
        .hero-cta:hover { background: #D14A1E; transform: translateY(-2px); box-shadow: 0 12px 32px rgba(232, 90, 42, 0.4); }

        .services { padding: 8rem 3rem; max-width: 1400px; margin: 0 auto; background: #000; }
        .section-header { text-align: center; margin-bottom: 5rem; }
        .section-header h2 { font-size: 3.5rem; line-height: 1.1; letter-spacing: -0.03em; font-weight: 700; color: #FFF; }
        .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .service-card { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; overflow: hidden; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); position: relative; cursor: pointer; }
        .service-card:hover { transform: translateY(-8px); box-shadow: 0 20px 60px rgba(232, 90, 42, 0.3); border-color: var(--orange); }
        .service-image { width: 100%; height: 200px; overflow: hidden; background: rgba(0, 0, 0, 0.5); }
        .service-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1); }
        .service-card:hover .service-image img { transform: scale(1.05); }
        .service-content { padding: 2rem; }
        .service-content h3 { font-size: 1.25rem; font-weight: 600; margin-bottom: 1rem; letter-spacing: -0.01em; color: #FFF; }
        .service-content p { font-size: 0.9375rem; line-height: 1.7; color: #CCC; margin-bottom: 1.5rem; }
        .service-link { color: var(--orange); font-weight: 600; cursor: pointer; transition: all 0.3s; display: inline-flex; align-items: center; gap: 0.5rem; font-size: 0.9375rem; }
        .service-link:hover { gap: 0.75rem; }

        .case-studies { padding: 8rem 3rem; background: #000; }
        .case-studies-container { max-width: 1400px; margin: 0 auto; }
        .case-studies-header { text-align: center; margin-bottom: 4rem; }
        .case-studies-header h2 { font-size: 3.5rem; font-weight: 700; margin-bottom: 1rem; letter-spacing: -0.03em; color: #FFF; }
        .case-studies-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2rem; }
        .case-card { background: rgba(255, 255, 255, 0.05); border-radius: 16px; overflow: hidden; cursor: pointer; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid rgba(255, 255, 255, 0.1); }
        .case-card:hover { transform: translateY(-8px); box-shadow: 0 20px 60px rgba(232, 90, 42, 0.3); border-color: var(--orange); }
        .case-image { width: 100%; height: 300px; overflow: hidden; position: relative; }
        .case-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1); }
        .case-card:hover .case-image img { transform: scale(1.05); }
        .case-label { position: absolute; top: 1.5rem; left: 1.5rem; background: var(--orange); color: #FFF; padding: 0.5rem 1rem; border-radius: 6px; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.1em; }
        .case-content { padding: 2rem; }
        .case-content h3 { font-size: 1.5rem; font-weight: 600; margin-bottom: 1rem; color: #FFF; letter-spacing: -0.02em; }

        .case-modal { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.95); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 2rem; }
        .case-modal-content { background: #1a1a1a; border-radius: 16px; max-width: 900px; width: 100%; max-height: 90vh; overflow-y: auto; position: relative; border: 1px solid rgba(255, 255, 255, 0.1); }
        .case-modal-close { position: absolute; top: 1.5rem; right: 1.5rem; background: rgba(255, 255, 255, 0.1); color: #FFF; border: none; width: 40px; height: 40px; border-radius: 50%; font-size: 1.5rem; cursor: pointer; z-index: 1; transition: all 0.3s; }
        .case-modal-close:hover { background: var(--orange); transform: rotate(90deg); }
        .case-modal-header { position: relative; height: 300px; overflow: hidden; }
        .case-modal-header img { width: 100%; height: 100%; object-fit: cover; }
        .case-modal-label { position: absolute; top: 1.5rem; left: 1.5rem; background: var(--orange); color: #FFF; padding: 0.5rem 1rem; border-radius: 6px; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.1em; }
        .case-modal-body { padding: 3rem; }
        .case-modal-title { font-size: 2.5rem; font-weight: 700; margin-bottom: 2rem; letter-spacing: -0.02em; color: #FFF; }
        .case-modal-section { margin-bottom: 2rem; }
        .case-modal-section h4 { font-size: 1.125rem; font-weight: 600; margin-bottom: 1rem; color: #FFF; }
        .case-modal-section p { line-height: 1.7; color: #CCC; font-size: 1rem; }
        .case-results { background: rgba(255, 255, 255, 0.05); padding: 2rem; border-radius: 12px; margin-bottom: 2rem; border: 1px solid rgba(255, 255, 255, 0.1); }
        .case-results h4 { font-size: 1.125rem; font-weight: 600; margin-bottom: 1rem; color: #FFF; }
        .case-results ul { list-style: none; display: grid; gap: 0.75rem; }
        .case-results li { padding-left: 2rem; position: relative; font-size: 0.9375rem; line-height: 1.6; color: #CCC; }
        .case-results li::before { content: '✓'; position: absolute; left: 0; color: var(--orange); font-weight: 700; font-size: 1.25rem; }
        .tech-tags { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .tech-tag { background: var(--orange); color: #FFF; padding: 0.5rem 1rem; border-radius: 6px; font-size: 0.8125rem; font-weight: 500; }
        .case-meta { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; margin-top: 2rem; padding-top: 2rem; border-top: 1px solid rgba(255, 255, 255, 0.1); }
        .case-meta-item { text-align: center; }
        .case-meta-item strong { display: block; font-size: 0.8125rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--text-secondary); margin-bottom: 0.5rem; font-weight: 600; }
        .case-meta-item span { font-size: 1rem; color: #FFF; font-weight: 500; }

        .testimonials { padding: 8rem 3rem; background: #000; color: #FFF; }
        .testimonials-container { max-width: 1400px; margin: 0 auto; }
        .testimonials-header { text-align: center; margin-bottom: 4rem; }
        .testimonials-header h2 { font-size: 3.5rem; font-weight: 700; letter-spacing: -0.03em; }
        .testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .testimonial-card { background: rgba(255, 255, 255, 0.05); backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.1); padding: 2.5rem; border-radius: 16px; position: relative; transition: all 0.4s; }
        .testimonial-card:hover { background: rgba(255, 255, 255, 0.08); border-color: var(--orange); transform: translateY(-4px); }
        .quote-mark { font-size: 4rem; line-height: 1; color: var(--orange); opacity: 0.3; margin-bottom: 1rem; }
        .testimonial-text { font-size: 1.0625rem; line-height: 1.7; margin-bottom: 1.5rem; color: #E5E5E5; }
        .testimonial-footer { display: flex; align-items: center; gap: 1rem; }
        .testimonial-role { font-size: 0.875rem; color: var(--text-secondary); }

        .forms-section { padding: 8rem 3rem; background: #0a0a0a; }
        .form-wrapper { max-width: 1400px; margin: 0 auto; }
        .form-tabs { display: flex; gap: 1rem; margin-bottom: 3rem; justify-content: center; }
        .form-tab { background: transparent; border: 2px solid rgba(255, 255, 255, 0.2); color: #FFF; padding: 1rem 2.5rem; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.3s; }
        .form-tab.active { background: var(--orange); border-color: var(--orange); color: #FFF; }
        .form-tab:hover:not(.active) { border-color: var(--orange); color: var(--orange); }
        .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; }
        .form-box { background: rgba(255, 255, 255, 0.05); padding: 3rem; border-radius: 16px; box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5); border: 1px solid rgba(255, 255, 255, 0.1); }
        .form-header { margin-bottom: 2rem; }
        .form-header h2 { font-size: 2.5rem; font-weight: 700; margin-bottom: 0.5rem; letter-spacing: -0.02em; color: #FFF; }
        .form-header p { color: #CCC; font-size: 1rem; }
        .form-group { margin-bottom: 1.5rem; }
        .form-group label { display: block; margin-bottom: 0.5rem; font-weight: 500; color: #FFF; font-size: 0.9375rem; }
        .form-group input, .form-group textarea, .form-group select { width: 100%; padding: 0.875rem 1rem; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 8px; font-size: 0.9375rem; font-family: inherit; transition: all 0.3s; background: rgba(255, 255, 255, 0.05); color: #FFF; }
        .form-group input:focus, .form-group textarea:focus, .form-group select:focus { outline: none; border-color: var(--orange); box-shadow: 0 0 0 3px rgba(232, 90, 42, 0.2); }
        .form-group textarea { resize: vertical; min-height: 120px; }
        .form-group.has-error input, .form-group.has-error textarea, .form-group.has-error select { border-color: #EF4444; }
        .form-error { color: #EF4444; font-size: 0.8125rem; margin-top: 0.5rem; display: flex; align-items: center; gap: 0.375rem; }
        .form-error::before { content: '!'; display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; background: #EF4444; color: #FFF; border-radius: 50%; font-size: 0.75rem; font-weight: 700; flex-shrink: 0; }
        .submit-btn { background: var(--orange); color: #FFF; padding: 1rem 2.5rem; border: none; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.3s; width: 100%; box-shadow: 0 4px 16px rgba(232, 90, 42, 0.3); display: flex; align-items: center; justify-content: center; gap: 0.5rem; min-height: var(--min-tap-target); }
        .submit-btn:hover:not(:disabled) { background: #D14A1E; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(232, 90, 42, 0.4); }
        .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
        .spinner { width: 20px; height: 20px; border: 2px solid rgba(255, 255, 255, 0.3); border-top-color: #FFF; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .form-confirmation { background: #10B981; color: #FFF; padding: 1rem; border-radius: 8px; text-align: center; margin-top: 1rem; font-weight: 500; }

        .newsletter { padding: 6rem 3rem; background: var(--orange); color: #000; text-align: center; }
        .newsletter h2 { font-size: 2.5rem; font-weight: 700; margin-bottom: 1rem; letter-spacing: -0.02em; color: #000; text-transform: uppercase; }
        .newsletter p { font-size: 1.125rem; color: #000; margin-bottom: 2rem; }
        .newsletter-form { display: flex; gap: 1rem; max-width: 600px; margin: 0 auto; }
        .newsletter-form input { flex: 1; padding: 1rem 1.5rem; border: 2px solid #000; border-radius: 8px; font-size: 1rem; background: #FFF; color: #000; }
        .newsletter-form button { padding: 1rem 2.5rem; background: #000; color: #FFF; border: 2px solid #000; border-radius: 8px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.3s; text-transform: uppercase; letter-spacing: 0.05em; }
        .newsletter-form button:hover { background: #FFF; color: #000; transform: translateY(-2px); }

        footer { background: #000; color: #FFF; padding: 4rem 3rem 2rem; }
        .footer-container { max-width: 1400px; margin: 0 auto; }
        .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 4rem; margin-bottom: 3rem; }
        .footer-brand h3 { font-size: 1.75rem; margin-bottom: 1rem; letter-spacing: 0.2em; color: var(--orange); font-weight: 700; }
        .footer-brand p { color: var(--text-secondary); line-height: 1.7; margin-bottom: 1.5rem; font-size: 0.9375rem; }
        .footer-social-buttons { display: flex; gap: 1rem; margin-top: 1.5rem; }
        .footer-social-btn { background: transparent; border: 2px solid #FFF; color: #FFF; padding: 0.75rem 1.5rem; border-radius: 6px; font-size: 0.875rem; font-weight: 600; cursor: pointer; transition: all 0.3s; text-transform: uppercase; letter-spacing: 0.05em; }
        .footer-social-btn:hover { background: var(--orange); border-color: var(--orange); }
        .footer-section h4 { font-size: 0.875rem; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 1.5rem; font-weight: 600; color: #CCC; }
        .footer-links { list-style: none; display: grid; gap: 0.75rem; }
        .footer-links a { color: var(--text-secondary); text-decoration: none; transition: color 0.3s; font-size: 0.9375rem; }
        .footer-links a:hover { color: var(--orange); }
        .footer-contact { display: grid; gap: 0.75rem; }
        .footer-contact-item { color: var(--text-secondary); font-size: 0.9375rem; }
        .footer-bottom { border-top: 1px solid rgba(255, 255, 255, 0.1); padding-top: 2rem; display: flex; justify-content: space-between; align-items: center; font-size: 0.875rem; color: var(--text-muted); }
        .footer-legal-links { display: flex; gap: 2rem; align-items: center; flex-wrap: wrap; }
        .footer-legal-links button { background: none; border: none; color: #666; font-size: 0.875rem; cursor: pointer; transition: color 0.3s; font-family: inherit; padding: 0; }
        .footer-legal-links button:hover { color: var(--orange); }

        /* Trust Section */
        .trust-section { padding: 6rem 3rem; background: #0a0a0a; text-align: center; }
        .trust-section h2 { font-size: 2.5rem; font-weight: 700; margin-bottom: 1rem; color: #FFF; }
        .trust-section .subtitle { font-size: 1.125rem; color: var(--text-secondary); margin-bottom: 3rem; }
        .trust-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1.5rem; max-width: 1000px; margin: 3rem auto 0; }
        .cert-badge { background: rgba(255, 255, 255, 0.05); padding: 2rem 1.5rem; border-radius: 12px; text-align: center; transition: all 0.3s; border: 2px solid rgba(255, 255, 255, 0.1); }
        .cert-badge:hover { transform: translateY(-4px); border-color: var(--orange); box-shadow: 0 8px 24px rgba(232, 90, 42, 0.3); }
        .cert-image-container { width: 80px; height: 80px; margin: 0 auto 1rem; display: flex; align-items: center; justify-content: center; }
        .cert-image { max-width: 100%; max-height: 100%; object-fit: contain; }
        .cert-name { font-size: 0.875rem; font-weight: 600; color: #FFF; }

        /* Back to Top Button */
        .back-to-top { position: fixed; bottom: 2rem; right: 2rem; z-index: 999; background: var(--orange); color: #FFF; border: none; width: 56px; height: 56px; border-radius: 50%; cursor: pointer; font-size: 1.5rem; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); transition: all 0.3s; }
        .back-to-top:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(232, 90, 42, 0.4); }

        /* Cookie Consent */
        .cookie-consent { position: fixed; bottom: 0; left: 0; right: 0; z-index: 9999; background: rgba(0, 0, 0, 0.98); backdrop-filter: blur(20px); border-top: 1px solid rgba(255, 255, 255, 0.1); padding: 1.5rem 2rem; }
        .cookie-content { max-width: 1400px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap; }
        .cookie-text { color: #CCC; font-size: 0.9375rem; line-height: 1.6; flex: 1; }
        .cookie-text a { color: var(--orange); text-decoration: underline; }
        .cookie-buttons { display: flex; gap: 1rem; flex-wrap: wrap; }
        .cookie-btn { padding: 0.75rem 1.5rem; border-radius: 8px; font-size: 0.875rem; font-weight: 600; cursor: pointer; transition: all 0.3s; border: none; text-transform: uppercase; letter-spacing: 0.05em; }
        .cookie-accept { background: var(--orange); color: #FFF; }
        .cookie-accept:hover { background: #D14A1E; transform: translateY(-2px); }
        .cookie-decline { background: transparent; color: #FFF; border: 2px solid rgba(255, 255, 255, 0.2); }
        .cookie-decline:hover { border-color: rgba(255, 255, 255, 0.5); }

        /* Policy Modal */
        .policy-modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.95); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 2rem; }
        .policy-modal-content { background: #1a1a1a; border-radius: 16px; max-width: 800px; width: 100%; max-height: 90vh; overflow-y: auto; position: relative; border: 1px solid rgba(255, 255, 255, 0.1); }
        .policy-modal-close { position: absolute; top: 1.5rem; right: 1.5rem; background: rgba(255, 255, 255, 0.1); color: #FFF; border: none; width: 40px; height: 40px; border-radius: 50%; font-size: 1.5rem; cursor: pointer; z-index: 1; transition: all 0.3s; }
        .policy-modal-close:hover { background: var(--orange); transform: rotate(90deg); }
        .policy-modal-header { padding: 3rem 3rem 0; border-bottom: 1px solid rgba(255, 255, 255, 0.1); padding-bottom: 2rem; }
        .policy-modal-header h2 { font-size: 2.5rem; font-weight: 700; color: #FFF; margin-bottom: 0.5rem; }
        .policy-last-updated { color: var(--text-secondary); font-size: 0.875rem; }
        .policy-modal-body { padding: 2rem 3rem 3rem; }
        .policy-section { margin-bottom: 2rem; }
        .policy-section:last-child { margin-bottom: 0; }
        .policy-section h3 { font-size: 1.25rem; font-weight: 600; color: #FFF; margin-bottom: 0.75rem; }
        .policy-section p { color: #CCC; line-height: 1.7; font-size: 0.9375rem; }

        /* Stats Dashboard */
        .stats-dashboard { background: linear-gradient(135deg, #000 0%, #1a1a1a 100%); padding: 6rem 3rem; color: #FFF; text-align: center; }
        .stats-dashboard h2 { font-size: 2.5rem; font-weight: 700; margin-bottom: 0.5rem; }
        .stats-dashboard .subtitle { font-size: 1.125rem; color: var(--text-secondary); margin-bottom: 3rem; }
        .stats-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3rem; max-width: 1000px; margin: 0 auto; }
        .stat-metric { text-align: center; }
        .stat-metric-value { font-size: 4rem; font-weight: 700; color: var(--orange); margin-bottom: 0.5rem; font-variant-numeric: tabular-nums; }
        .stat-metric-label { font-size: 1.125rem; color: #CCC; }
        .stat-metric-sublabel { font-size: 0.875rem; color: var(--text-muted); margin-top: 0.25rem; }

        /* FAQ Section */
        .faq-section { padding: 6rem 3rem; max-width: 900px; margin: 0 auto; background: #000; }
        .faq-section h2 { font-size: 2.5rem; font-weight: 700; text-align: center; margin-bottom: 3rem; color: #FFF; }
        .faq-list { display: grid; gap: 1rem; }
        .faq-item { background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; overflow: hidden; transition: all 0.3s; }
        .faq-item.active { border-color: var(--orange); box-shadow: 0 4px 20px rgba(232, 90, 42, 0.3); }
        .faq-question { width: 100%; padding: 1.5rem 2rem; cursor: pointer; display: flex; justify-content: space-between; align-items: center; font-weight: 600; font-size: 1.0625rem; color: #FFF; transition: all 0.3s; background: none; border: none; font-family: inherit; text-align: left; }
        .faq-question:hover { background: rgba(255, 255, 255, 0.08); color: var(--orange); }
        .faq-icon { font-size: 1.5rem; transition: transform 0.3s; color: var(--orange); }
        .faq-item.active .faq-icon { transform: rotate(45deg); }
        .faq-answer { padding: 0 2rem; max-height: 0; overflow: hidden; transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
        .faq-item.active .faq-answer { padding: 0 2rem 1.5rem; max-height: 500px; }
        .faq-answer p { color: #CCC; line-height: 1.7; font-size: 0.9375rem; }

        /* Blog Section */
        .blog-section { background: #0a0a0a; padding: 6rem 3rem; }
        .blog-section h2 { font-size: 2.5rem; font-weight: 700; text-align: center; margin-bottom: 3rem; color: #FFF; }
        .blog-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; max-width: 1400px; margin: 0 auto; }
        .blog-card { background: rgba(255, 255, 255, 0.05); border-radius: 12px; overflow: hidden; transition: all 0.3s; border: 1px solid rgba(255, 255, 255, 0.1); text-decoration: none; color: inherit; display: block; }
        .blog-card:hover { transform: translateY(-8px); box-shadow: 0 12px 40px rgba(232, 90, 42, 0.3); border-color: var(--orange); }
        .blog-image { width: 100%; height: 200px; overflow: hidden; }
        .blog-image img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s; }
        .blog-card:hover .blog-image img { transform: scale(1.1); }
        .blog-content { padding: 1.5rem; }
        .blog-meta { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; font-size: 0.8125rem; color: var(--text-secondary); }
        .blog-category { background: var(--orange); color: #FFF; padding: 0.25rem 0.75rem; border-radius: 4px; font-weight: 600; }
        .blog-title { font-size: 1.125rem; font-weight: 600; color: #FFF; line-height: 1.4; }

        /* Responsive Styles */
        @media (max-width: 1023px) {
          .services-grid { grid-template-columns: repeat(2, 1fr); }
          .case-studies-grid { grid-template-columns: repeat(2, 1fr); }
          .testimonials-grid { grid-template-columns: repeat(2, 1fr); }
          .footer-grid { grid-template-columns: 1fr 1fr; gap: 3rem; }
          .trust-grid { grid-template-columns: repeat(5, 1fr); gap: 1rem; }
        }

        @media (max-width: 899px) {
          .nav-links { display: none; }
          .mobile-menu-btn { display: flex; }
          .nav-container { padding: 1rem 1.5rem; grid-template-columns: 1fr auto; gap: 1rem; }
          .hero { min-height: auto; padding: 120px 0 80px; }
          .hero-content h1 { font-size: 2.75rem; }
          .services-grid { grid-template-columns: repeat(2, 1fr); }
          .testimonials-grid { grid-template-columns: 1fr; }
          .form-grid { grid-template-columns: 1fr; gap: 2rem; }
        }

        @media (max-width: 767px) {
          .hero-content h1 { font-size: 2.25rem; }
          .hero-content { padding: 3rem 1.5rem; }
          .services { padding: 4rem 1.5rem; }
          .case-studies { padding: 4rem 1.5rem; }
          .testimonials { padding: 4rem 1.5rem; }
          .forms-section { padding: 4rem 1.5rem; }
          .trust-section { padding: 4rem 1.5rem; }
          .stats-dashboard { padding: 4rem 1.5rem; }
          .faq-section { padding: 4rem 1.5rem; }
          .blog-section { padding: 4rem 1.5rem; }
          .newsletter { padding: 4rem 1.5rem; }
          footer { padding: 3rem 1.5rem 2rem; }
          .services-grid { grid-template-columns: 1fr; }
          .case-studies-grid { grid-template-columns: 1fr; }
          .blog-grid { grid-template-columns: 1fr; }
          .trust-grid { grid-template-columns: repeat(2, 1fr); }
          .stats-metrics { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: 1fr; text-align: center; }
          .footer-social-buttons { justify-content: center; }
          .newsletter-form { flex-direction: column; }
          .footer-bottom { flex-direction: column; gap: 1.5rem; text-align: center; }
          .footer-bottom > div:last-child { flex-direction: column; gap: 1rem; }
          .cookie-consent { padding: 1rem 1.5rem; }
          .nav { backdrop-filter: none; background: rgba(0, 0, 0, 0.98); }
          .testimonial-card { backdrop-filter: none; }
          .cookie-consent { backdrop-filter: none; background: rgba(0, 0, 0, 0.98); }
          .policy-modal-overlay { padding: 1rem; }
          .policy-modal-header { padding: 2rem 1.5rem 1.5rem; }
          .policy-modal-header h2 { font-size: 1.75rem; }
          .policy-modal-body { padding: 1.5rem; }
          .footer-legal-links { gap: 1rem; justify-content: center; }
        }

        @media (max-width: 479px) {
          .hero-content h1 { font-size: 1.875rem; }
          .section-header h2 { font-size: 2rem; }
          .case-modal { padding: 1rem; }
          .case-modal-body { padding: 1.25rem; }
          .case-modal-title { font-size: 1.5rem; }
          .case-meta { grid-template-columns: 1fr; gap: 1rem; }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Print styles */
        @media print {
          .nav, .mobile-menu, .back-to-top, .cookie-consent, .scroll-progress { display: none !important; }
        }
      `}</style>

      <AnimatePresence>
        <motion.div className="scroll-progress" style={{ scaleX }} />
      </AnimatePresence>

      {/* Skip to Content Link */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          top: '-100px',
          left: '0',
          background: 'var(--orange)',
          color: '#FFF',
          padding: '1rem 2rem',
          textDecoration: 'none',
          fontWeight: 600,
          zIndex: 10001,
          transition: 'top 0.3s'
        }}
        onFocus={(e) => e.target.style.top = '0'}
        onBlur={(e) => e.target.style.top = '-100px'}
      >
        Skip to main content
      </a>

      <Navigation
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        scrollToSection={scrollToSection}
      />

      <Hero scrollToSection={scrollToSection} />

      <Suspense fallback={<SectionLoader />}>
        <TrustSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <StatsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Services
          selectedService={selectedService}
          setSelectedService={setSelectedService}
        />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <CaseStudies
          selectedCaseStudy={selectedCaseStudy}
          setSelectedCaseStudy={setSelectedCaseStudy}
        />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Testimonials />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <FormsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <FAQ />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Blog />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Newsletter />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <Footer scrollToSection={scrollToSection} onPolicyClick={setSelectedPolicy} />
      </Suspense>

      <Suspense fallback={null}>
        <BackToTop />
      </Suspense>

      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>

      <Suspense fallback={null}>
        {selectedPolicy && (
          <PolicyModal policy={selectedPolicy} onClose={() => setSelectedPolicy(null)} />
        )}
      </Suspense>
    </div>
  );
}
